import { Injectable } from '@angular/core';

export interface BorderValues {
  color?: string;
  style?: string;
  valueAll?: string;
  valueTop?: string;
  valueRight?: string;
  valueBottom?: string;
  valueLeft?: string;
}

export interface BorderRadiusValues {
  valueAll?: string;
  valueTop?: string;
  valueRight?: string;
  valueBottom?: string;
  valueLeft?: string;
  topLeft?: string;
  topRight?: string;
  bottomRight?: string;
  bottomLeft?: string;
}

export interface BoxShadowValues {
  offsetX?: string;
  offsetY?: string;
  blurRadius?: string;
  spreadRadius?: string;
  color?: string;
  inset?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class CssPropertyParserService {

  /**
   * Parses a border property string into individual values
   * Supports formats like:
   * - "2px solid #ccc"
   * - "2px 1px solid #ccc"
   * - "2px 3px 1px 5px solid #ccc"
   */
  parseBorder(borderValue: string): BorderValues {
    if (!borderValue || typeof borderValue !== 'string') {
      return {};
    }

    const trimmed = borderValue.trim();
    const parts = trimmed.split(/\s+/);
    
    const result: BorderValues = {};
    const widthValues: string[] = [];
    
    // Common border styles
    const borderStyles = [
      'none', 'hidden', 'dotted', 'dashed', 'solid', 'double', 
      'groove', 'ridge', 'inset', 'outset', 'initial', 'inherit'
    ];
    
    // Parse each part
    for (const part of parts) {
      if (this.isColor(part)) {
        result.color = part;
      } else if (borderStyles.includes(part.toLowerCase())) {
        result.style = part;
      } else if (this.isDimension(part)) {
        widthValues.push(part);
      }
    }
    
    // Process width values based on count
    if (widthValues.length === 1) {
      result.valueAll = widthValues[0];
      result.valueTop = result.valueRight = result.valueBottom = result.valueLeft = widthValues[0];
    } else if (widthValues.length === 2) {
      result.valueTop = result.valueBottom = widthValues[0];
      result.valueRight = result.valueLeft = widthValues[1];
    } else if (widthValues.length === 3) {
      result.valueTop = widthValues[0];
      result.valueRight = result.valueLeft = widthValues[1];
      result.valueBottom = widthValues[2];
    } else if (widthValues.length === 4) {
      result.valueTop = widthValues[0];
      result.valueRight = widthValues[1];
      result.valueBottom = widthValues[2];
      result.valueLeft = widthValues[3];
    }
    
    return result;
  }

  /**
   * Parses a border-radius property string into individual values
   * Supports formats like:
   * - "10px"
   * - "10px 20px"
   * - "10px 20px 30px"
   * - "10px 20px 30px 40px"
   * - "10px 20px / 30px 40px" (elliptical borders)
   */
  parseBorderRadius(radiusValue: string): BorderRadiusValues {
    if (!radiusValue || typeof radiusValue !== 'string') {
      return {};
    }

    const trimmed = radiusValue.trim();
    const result: BorderRadiusValues = {};
    
    // Check if it contains elliptical syntax (/)
    const parts = trimmed.split('/').map(part => part.trim());
    const horizontalValues = parts[0].split(/\s+/).filter(v => v);
    const verticalValues = parts[1] ? parts[1].split(/\s+/).filter(v => v) : [];
    
    // Process horizontal values
    this.processRadiusValues(horizontalValues, result, false);
    
    // Process vertical values if present
    if (verticalValues.length > 0) {
      this.processRadiusValues(verticalValues, result, true);
    }
    
    // Set valueAll if all corners are the same
    if (result.topLeft === result.topRight && 
        result.topRight === result.bottomRight && 
        result.bottomRight === result.bottomLeft) {
      result.valueAll = result.topLeft;
    }
    
    return result;
  }

  /**
   * Parses a box-shadow property string into individual values
   * Supports formats like:
   * - "2px 4px 6px #000"
   * - "inset 2px 4px 6px 2px rgba(0,0,0,0.3)"
   * - "0 0 10px rgba(255,255,255,0.5)"
   */
  parseBoxShadow(shadowValue: string): BoxShadowValues {
    if (!shadowValue || typeof shadowValue !== 'string') {
      return {};
    }

    const trimmed = shadowValue.trim();
    const parts = this.splitBoxShadowParts(trimmed);
    const result: BoxShadowValues = {};
    
    let dimensionIndex = 0;
    
    for (const part of parts) {
      if (part.toLowerCase() === 'inset') {
        result.inset = true;
      } else if (this.isColor(part)) {
        result.color = part;
      } else if (this.isDimension(part) || part === '0') {
        switch (dimensionIndex) {
          case 0:
            result.offsetX = part;
            break;
          case 1:
            result.offsetY = part;
            break;
          case 2:
            result.blurRadius = part;
            break;
          case 3:
            result.spreadRadius = part;
            break;
        }
        dimensionIndex++;
      }
    }
    
    return result;
  }

  private processRadiusValues(values: string[], result: BorderRadiusValues, isVertical: boolean = false): void {
    const suffix = isVertical ? 'Vertical' : '';
    
    if (values.length === 1) {
      const value = values[0];
      result[`topLeft${suffix}` as keyof BorderRadiusValues] = value;
      result[`topRight${suffix}` as keyof BorderRadiusValues] = value;
      result[`bottomRight${suffix}` as keyof BorderRadiusValues] = value;
      result[`bottomLeft${suffix}` as keyof BorderRadiusValues] = value;
      if (!isVertical) {
        result.valueAll = value;
        result.valueTop = result.valueRight = result.valueBottom = result.valueLeft = value;
      }
    } else if (values.length === 2) {
      result[`topLeft${suffix}` as keyof BorderRadiusValues] = values[0];
      result[`topRight${suffix}` as keyof BorderRadiusValues] = values[1];
      result[`bottomRight${suffix}` as keyof BorderRadiusValues] = values[0];
      result[`bottomLeft${suffix}` as keyof BorderRadiusValues] = values[1];
      if (!isVertical) {
        result.valueTop = result.valueBottom = values[0];
        result.valueRight = result.valueLeft = values[1];
      }
    } else if (values.length === 3) {
      result[`topLeft${suffix}` as keyof BorderRadiusValues] = values[0];
      result[`topRight${suffix}` as keyof BorderRadiusValues] = values[1];
      result[`bottomRight${suffix}` as keyof BorderRadiusValues] = values[2];
      result[`bottomLeft${suffix}` as keyof BorderRadiusValues] = values[1];
      if (!isVertical) {
        result.valueTop = values[0];
        result.valueRight = result.valueLeft = values[1];
        result.valueBottom = values[2];
      }
    } else if (values.length === 4) {
      result[`topLeft${suffix}` as keyof BorderRadiusValues] = values[0];
      result[`topRight${suffix}` as keyof BorderRadiusValues] = values[1];
      result[`bottomRight${suffix}` as keyof BorderRadiusValues] = values[2];
      result[`bottomLeft${suffix}` as keyof BorderRadiusValues] = values[3];
      if (!isVertical) {
        result.valueTop = values[0];
        result.valueRight = values[1];
        result.valueBottom = values[2];
        result.valueLeft = values[3];
      }
    }
  }

  private splitBoxShadowParts(value: string): string[] {
    const parts: string[] = [];
    let current = '';
    let inFunction = 0;
    let inQuotes = false;
    
    for (let i = 0; i < value.length; i++) {
      const char = value[i];
      
      if (char === '"' || char === "'") {
        inQuotes = !inQuotes;
        current += char;
      } else if (!inQuotes && char === '(') {
        inFunction++;
        current += char;
      } else if (!inQuotes && char === ')') {
        inFunction--;
        current += char;
      } else if (!inQuotes && inFunction === 0 && char === ' ') {
        if (current.trim()) {
          parts.push(current.trim());
          current = '';
        }
      } else {
        current += char;
      }
    }
    
    if (current.trim()) {
      parts.push(current.trim());
    }
    
    return parts;
  }

  private isColor(value: string): boolean {
    // Check for hex colors
    if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(value)) {
      return true;
    }
    
    // Check for rgb/rgba functions
    if (/^rgba?\s*\(/i.test(value)) {
      return true;
    }
    
    // Check for hsl/hsla functions
    if (/^hsla?\s*\(/i.test(value)) {
      return true;
    }
    
    // Check for named colors (common ones)
    const namedColors = [
      'transparent', 'black', 'white', 'red', 'green', 'blue', 'yellow', 
      'orange', 'purple', 'pink', 'brown', 'gray', 'grey', 'cyan', 'magenta',
      'lime', 'maroon', 'navy', 'olive', 'silver', 'teal', 'aqua', 'fuchsia'
    ];
    
    return namedColors.includes(value.toLowerCase());
  }

  private isDimension(value: string): boolean {
    // Check for valid CSS dimensions (px, em, rem, %, vh, vw, etc.)
    return /^-?\d*\.?\d+(px|em|rem|%|vh|vw|vmin|vmax|cm|mm|in|pt|pc|ex|ch)$/i.test(value);
  }
  // Helper method to extract numerical value from CSS string (e.g., "5px" -> 5)
  extractNumber(value: string): number {
    if (!value) return 0;
    const numericValue = parseFloat(value);
    return isNaN(numericValue) ? 0 : numericValue;
  }
  // Helper method to extract unit of measure from CSS string (e.g., "5px" -> "px")
  extractUnit(value: string): string {
    if (!value) return 'px'; // default unit
    const match = value.match(/[a-z%]+$/i);
    return match ? match[0] : 'px'; // return matched unit or default to 'px'
  }
  getUnitFromResult(result: any): string {
    console.log("getUnitFromResult: "+result)
    for (const key in result) {
      if (result.hasOwnProperty(key)) {
        const value = result[key];
        if (value && value !== '') {
          return this.extractUnit(value);
        }
      }
    }
    return 'px'; // default if all properties are empty
  }
}
